// banner.service.ts
import { Injectable } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { generateUUID } from 'src/app/shared/utils/uuid';

// banner.model.ts
export type BannerLevel = 'info' | 'success' | 'warning' | 'error';
export type CtaType = 'primary' | 'secondary' | 'link';

export interface BannerCta {
  type: CtaType;
  i18nKey?: string; // optional if you pass plain label
  label?: string; // fallback/explicit label
  icon?: IconProp; // optional icon
  action: string; // e.g. 'auth:login', 'page:refresh'
  meta?: any;
}

export interface BannerMessage {
  id: string;
  level: BannerLevel;
  message: string;
  details?: any; // optional details object (will be stringified)
  i18nParams?: Record<string, any>;
  ctAs?: BannerCta[]; // support multiple CTAs
  sticky?: boolean; // errors/warnings default sticky
  scope?: string; // replace existing banner with same scope
  createdAt: number;
  autoCloseMs?: number; // non-sticky info/success default 7s
  focusOnShow?: boolean; // for assertive banners
  variant?: 'bar' | 'dialog';
}

// Interceptor → Banner bridge types
export type InterceptorBannerLevel = 'info' | 'success' | 'warning' | 'error';
export type InterceptorCtaType = 'primary' | 'secondary' | 'link';

export interface InterceptorCta {
  type: InterceptorCtaType;
  i18nKey?: string;
  action: string;
  meta?: any; // e.g. { retryKey: string }
  label?: string; // fallback label if no i18n
}

export interface InterceptorBannerPayload {
  level: InterceptorBannerLevel;
  message?: string; // i18n key
  details?: any; // optional details object (will be stringified)
  // Either one CTA or a group (interceptor can send both; group wins)
  cta?: InterceptorCta;
  ctaGroup?: InterceptorCta[];

  // Optional extras the interceptor may provide
  retryAfterSeconds?: number; // used to hint auto-close duration for info/success
  scope?: string; // replace existing banner in same scope
  sticky?: boolean; // force stickiness
  focusOnShow?: boolean; // force focus
  autoCloseMs?: number; // explicit override
}

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly _banners$ = new BehaviorSubject<BannerMessage[]>([]);
  private readonly timers = new Map<string, any>(); // id -> timeout

  /** Stream of current banners (latest-first) */
  readonly banners$: Observable<BannerMessage[]> =
    this._banners$.asObservable();

  /** Show a banner (returns its id) */
  show(partial: Omit<BannerMessage, 'id' | 'createdAt'>): string {
    const id = generateUUID();
    const banner: BannerMessage = {
      id,
      createdAt: Date.now(),
      focusOnShow:
        partial.level === 'error' || partial.level === 'warning'
          ? true
          : partial.focusOnShow,
      autoCloseMs: this.defaultAutoClose(
        partial.level,
        partial.sticky,
        partial.autoCloseMs,
      ),
      ...partial,
      variant: 'dialog', // default variant
    };

    this._banners$.next(
      this.insertReplacingScope(banner, this._banners$.value),
    );

    if (banner.autoCloseMs && !banner.sticky) {
      const t = setTimeout(() => this.dismiss(id), banner.autoCloseMs);
      this.timers.set(id, t);
    }

    return id;
  }

  /** Helpers for convenience */
  info(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({ level: 'info', message, ...extras });
  }
  success(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'success',
      message: message,
      ...extras,
    });
  }
  warning(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'warning',
      message: message,
      sticky: true,
      ...extras,
    });
  }
  error(
    message: string,
    extras: Partial<Omit<BannerMessage, 'id' | 'createdAt' | 'level'>> = {},
  ) {
    return this.show({
      level: 'error',
      message: message,
      sticky: true,
      ...extras,
    });
  }

  dismissAll(): void {
    this.clear();
  }
  /** Dismiss single banner */
  dismiss(id: string): void {
    const list = this._banners$.value.filter((b) => b.id !== id);
    this.clearTimer(id);
    this._banners$.next(list);
  }

  /** Clear all or by scope */
  clear(scope?: string): void {
    const list = scope
      ? this._banners$.value.filter((b) => b.scope !== scope)
      : [];
    // cleanup timers for removed items
    const removedIds = this._banners$.value
      .filter((b) => !list.some((nb) => nb.id === b.id))
      .map((b) => b.id);
    removedIds.forEach((id) => this.clearTimer(id));
    this._banners$.next(list);
  }

  // ---- internals ----
  private insertReplacingScope(
    banner: BannerMessage,
    current: BannerMessage[],
  ) {
    const filtered = banner.scope
      ? current.filter((b) => b.scope !== banner.scope)
      : current.slice();
    return [banner, ...filtered]; // newest first
  }

  private defaultAutoClose(
    level: BannerLevel,
    sticky?: boolean,
    override?: number,
  ) {
    if (typeof override === 'number') return override;
    if (sticky || level === 'error' || level === 'warning') return undefined;
    return 7000; // 7s for info/success
  }

  private clearTimer(id: string) {
    const t = this.timers.get(id);
    if (t) {
      clearTimeout(t);
      this.timers.delete(id);
    }
  }

  /**
   * Adapter for interceptor → banner service.
   * Accepts the standardized payload from the HTTP interceptor and renders it.
   */
  showBanner(payload: InterceptorBannerPayload): string {
    // Prefer ctaGroup; fallback to single cta → array
    const ctAs = payload.ctaGroup ?? (payload.cta ? [payload.cta] : undefined);

    // Decide stickiness / autoclose:
    // - error/warning default sticky
    // - info/success default 7s (or retryAfterSeconds hint if provided)
    const defaultSticky =
      payload.level === 'error' || payload.level === 'warning';
    const sticky = payload.sticky ?? defaultSticky;

    let autoCloseMs: number | undefined = payload.autoCloseMs;
    if (autoCloseMs === undefined && !sticky) {
      autoCloseMs =
        typeof payload.retryAfterSeconds === 'number'
          ? Math.max(3000, payload.retryAfterSeconds * 1000) // don't blink too fast
          : 7000;
    }

    const focusOnShow =
      payload.focusOnShow ??
      (payload.level === 'error' || payload.level === 'warning');

    // Map to internal shape and delegate to existing `show(...)`
    return this.show({
      level: payload.level,
      message: payload.message,
      ctAs: ctAs?.map((c) => ({
        type: c.type,
        i18nKey: c.i18nKey,
        label: c.label,
        action: c.action,
        meta: c.meta,
      })),
      details: payload.details,
      scope: payload.scope,
      sticky,
      autoCloseMs,
      focusOnShow,
    });
  }
}
